import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { BodyText, theme, VStack } from '@sg/garnish';

import { getHostName } from '@order/utils';

import { type PartialLineItem } from '../../graphql/partials';
import { useFavoriteLineItem } from '../../hooks/useFavoriteLineItem';
import { useShareLineItem } from '../../hooks/useShareLineItem';
import { OrderStatusLineItemCard } from './components/OrderStatusLineItemCard';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusLineItems = (props: OrderStatusLineItemsProps) => {
  const { lineItems, onReorderLineItem, onReorderConflict } = props;

  const { formatMessage } = useIntl();

  const { isFavoringLineItem, handleFavoriteLineItem } = useFavoriteLineItem();
  const { handleShareLineItem } = useShareLineItem();

  return (
    <VStack gap={0}>
      <BodyText sizeMatch={['16']} bold>
        <FormattedMessage {...messages.yourOrder} />
      </BodyText>

      <VStack gap={0} hasDivider={true} dividerColor={theme.colors.LIGHT}>
        {lineItems.map((lineItem) => {
          const url = `${getHostName()}/share/${lineItem.slug}`;

          return (
            <OrderStatusLineItemCard
              key={lineItem.id ?? lineItem.product.name}
              lineItem={lineItem}
              onFavorite={handleFavoriteLineItem}
              isFetchingFavorite={isFavoringLineItem}
              isFavorite={lineItem.favorited}
              onShare={handleShareLineItem}
              shareBtnLabel={formatMessage(messages.shareProduct)}
              shareTooltipLabel={formatMessage(messages.shareProductTooltip)}
              shareUrl={url}
              onReorderLineItem={onReorderLineItem}
              onReorderConflict={onReorderConflict}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  yourOrder: {
    defaultMessage: 'Your order',
    description: 'Order Status | Line Items | Your order heading',
  },
  shareProduct: {
    defaultMessage: 'Share',
    description: 'Order Status | Line Items | Share Product',
  },
  shareProductTooltip: {
    defaultMessage: 'Copied link',
    description: 'Order Status | Line Items | Share Product Tooltip',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type OrderStatusLineItemsProps = {
  lineItems: readonly PartialLineItem[];
  onReorderLineItem?: (lineItemId: string, force: boolean) => Promise<void>;
  onReorderConflict?: (lineItem: Omit<PartialLineItem, 'ingredients'>) => void;
};
